/**
 * Ad set schedule time contract (edit flow, launch, DynamoDB):
 *
 * - **Persist** `start_time` / `stop_time` as UTC ISO (`…Z`) via {@link scheduleInputToUtcIso}.
 * - **Interpret naive strings** (no offset) as wall clock in the event IANA zone, not server/browser local.
 * - **Forms / GET payloads** use {@link scheduleInputToEventWall} for `YYYY-MM-DDTHH:mm` in the event zone.
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const OFFSET_OR_Z_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Single instant from a schedule string: UTC ISO / offset → absolute; naive → wall in `eventIana`.
 */
function parseScheduleInputInstant(
  value: string,
  eventIana: string
): dayjs.Dayjs | null {
  const v = value.trim();
  if (!v) return null;
  const tz = eventIana.trim();
  const parsed = OFFSET_OR_Z_SUFFIX.test(v) ? dayjs(v) : dayjs.tz(v, tz);
  return parsed.isValid() ? parsed : null;
}

/** Absolute instant for comparisons (same rules as {@link scheduleInputToUtcIso}). */
export function scheduleInputToInstant(
  value: string,
  eventIana: string
): dayjs.Dayjs | null {
  return parseScheduleInputInstant(value, eventIana);
}

/**
 * Parse an ad set schedule timestamp for persistence as UTC ISO-8601 (`…Z`).
 * Values with `Z` or a numeric offset are absolute instants.
 * Naive strings (no offset) are wall clock in `timezone` (IANA).
 */
export function scheduleInputToUtcIso(
  value: string | undefined | null,
  timezone: string
): string | undefined {
  if (value == null || String(value).trim() === "") return undefined;
  const inst = parseScheduleInputInstant(String(value).trim(), timezone);
  return inst?.toISOString();
}

/**
 * Stored UTC ISO or legacy naive wire → wall clock in `eventIana` for forms and API GET payloads.
 */
export function scheduleInputToEventWall(
  value: string | undefined | null,
  eventIana: string,
  format: string = "YYYY-MM-DDTHH:mm"
): string {
  if (value == null || String(value).trim() === "") return "";
  const tz = eventIana.trim();
  const inst = parseScheduleInputInstant(String(value).trim(), tz);
  if (!inst) return "";
  return inst.tz(tz).format(format);
}

/**
 * Backward-compatible aliases; prefer canonical names above in new code.
 */
export const adsetScheduleTimeToUtcIso = scheduleInputToUtcIso;
export const eventWallOrIsoToUtcIso = scheduleInputToUtcIso;
export const utcIsoOrWireToEventWall = scheduleInputToEventWall;
export const scheduleWireOrIsoToUtcInstant = scheduleInputToInstant;

/**
 * Normalize schedule before writing to storage so `start_time` / `stop_time` are UTC ISO.
 * Uses **root (event) timezone first** for naive parsing so ad set schedule matches the linked event;
 * falls back to `schedule.timezone` only when root is empty.
 */
export function normalizeAdsetScheduleForPersist<
  T extends {
    start_time?: string | null;
    stop_time?: string | null;
    timezone?: string | null;
  },
>(
  schedule: T | undefined | null,
  rootTimezone: string | undefined | null
): T | undefined | null {
  if (schedule == null) return schedule;
  const tz = (rootTimezone ?? "").trim() || schedule.timezone?.trim() || "UTC";
  const next = { ...schedule } as T;
  if (next.start_time != null && String(next.start_time).trim() !== "") {
    next.start_time =
      scheduleInputToUtcIso(next.start_time, tz) ?? next.start_time;
  }
  if (next.stop_time != null && String(next.stop_time).trim() !== "") {
    next.stop_time =
      scheduleInputToUtcIso(next.stop_time, tz) ?? next.stop_time;
  }
  (next as { timezone?: string | null }).timezone = tz;
  return next;
}

/** Minimal ad set shape for comparing `schedule.stop_time` to “now”. */
export type AdsetWithStopSchedule = {
  schedule?: { stop_time?: string | null; timezone?: string | null } | null;
  timezone?: string | null;
};

/**
 * Whether `adset.schedule.stop_time` is strictly before `now`, interpreting naive
 * `stop_time` values in `timezone` (strings with Z/offset use absolute time).
 *
 * Shared by dashboard API (edit flow, lifecycle) and customer app display logic.
 *
 * `now` is typed as `unknown` so callers may pass any dayjs instance (UTC/tz plugins
 * change the `Dayjs` static type across packages).
 */
export function isAdsetScheduleEnded(params: {
  adset: AdsetWithStopSchedule;
  timezone: string;
  now: unknown;
}): boolean {
  const { adset, timezone: tz, now } = params;
  const stopTime = adset.schedule?.stop_time;
  if (!stopTime) return false;
  const stopInstant = parseScheduleInputInstant(stopTime, tz);
  if (!stopInstant) return false;
  return stopInstant.isBefore(now as dayjs.Dayjs);
}

function resolveAdsetRecordTimezone(adset: AdsetWithStopSchedule): string {
  const root = adset.timezone?.trim();
  const fromSchedule = adset.schedule?.timezone?.trim();
  return root || fromSchedule;
}

/**
 * UI helper: “ended” if stop is before current instant in **this ad set’s** stored
 * timezone (root or `schedule.timezone`), else `UTC`.
 */
export function isDisplayAdsetScheduleEnded(
  adset?: AdsetWithStopSchedule | null
): boolean {
  if (!adset) return false;
  const tz = resolveAdsetRecordTimezone(adset);
  const now = dayjs.tz(new Date(), tz);
  return isAdsetScheduleEnded({ adset, timezone: tz, now });
}

/** Minimal row shape for “can we open budget/schedule/campaign edit on an ad set?” gating in the dashboard. */
export type ManagedAdsetLikeForEditGate = AdsetWithStopSchedule & {
  status?: string | null;
};

export type ManagedAdsetLikeForGuidedEditPick = ManagedAdsetLikeForEditGate & {
  id?: string | null;
};

/**
 * Default guided-edit GET anchor: visible (not deleted / to-delete).
 * Prefers an ad set whose delivery window has not ended (in list order); if every visible
 * ad set has ended, returns the first visible one so those rows remain editable.
 */
export function pickDefaultManagedAdsetIdForGuidedEdit(
  adsets: readonly ManagedAdsetLikeForGuidedEditPick[] | null | undefined
): string | null {
  const list = adsets ?? [];
  let firstVisibleId: string | null = null;
  for (const a of list) {
    if (a.status === "DELETED" || a.status === "TO_DELETE") continue;
    const id = a.id != null ? String(a.id).trim() : "";
    if (!id) continue;
    if (firstVisibleId == null) firstVisibleId = id;
    if (!isDisplayAdsetScheduleEnded(a)) return id;
  }
  return firstVisibleId;
}

export type ManagedAdsetEditUnavailable =
  | { blocked: false }
  | { blocked: true; reason: "no_adsets" | "all_removed" };

/**
 * When there is no non-deleted ad set to anchor guided edit (empty list or all removed).
 */
export function resolveManagedAdsetEditUnavailable(
  adsets: readonly ManagedAdsetLikeForEditGate[] | null | undefined
): ManagedAdsetEditUnavailable {
  const list = adsets ?? [];
  const visible = list.filter(
    (a) => a.status !== "DELETED" && a.status !== "TO_DELETE"
  );
  if (visible.length === 0) {
    return {
      blocked: true,
      reason: list.length === 0 ? "no_adsets" : "all_removed",
    };
  }
  return { blocked: false };
}

/** User-facing explanation when {@link resolveManagedAdsetEditUnavailable} is blocked (dashboard + GET guided-edit). */
export function messageForManagedAdsetEditUnavailable(
  info: Extract<ManagedAdsetEditUnavailable, { blocked: true }>
): string {
  switch (info.reason) {
    case "no_adsets":
      return "This campaign doesn't have any ad sets yet. Create one first, then you can edit it here.";
    case "all_removed":
      return "Every ad set was removed from this campaign. There's nothing left to edit.";
  }
}

const GUIDED_EDIT_NOTHING_TO_LOAD_FETCH_MESSAGES: ReadonlySet<string> = new Set([
  messageForManagedAdsetEditUnavailable({ blocked: true, reason: "no_adsets" }),
  messageForManagedAdsetEditUnavailable({ blocked: true, reason: "all_removed" }),
]);

/**
 * True when guided-edit GET failed with the expected 400 "no ad sets to edit" response
 * (no ad sets or all removed), not eligibility or transport errors.
 */
export function isGuidedEditNothingToLoadFetchError(err: unknown): boolean {
  if (err == null || typeof err !== "object") return false;
  const rec = err as { statusCode?: unknown; message?: unknown };
  if (rec.statusCode !== 400 || typeof rec.message !== "string") return false;
  return GUIDED_EDIT_NOTHING_TO_LOAD_FETCH_MESSAGES.has(rec.message);
}
